/**
 * DBN Pipeline — Velocity Intelligence + Kanban Board + Table View + MSME Readiness Scorecard
 */
import React, { useState, useEffect } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis } from "recharts";
import { dbnApi, PageHeader, SectionHeader, Badge, LoadingState, fmtEur, fmtN,
  scoreColor, riskColor, ProgressBar } from "./dbnUtils";import { TrendingUp, AlertTriangle, Search, Clock, ArrowRight, ChevronUp } from "lucide-react";

const STAGE_ORDER = ["Readiness Assessment","Technical Assistance","PFI Review","Due Diligence","Approved","Disbursed","Monitoring"];
const STAGE_COLORS = {
  "Readiness Assessment":"#64748b","Technical Assistance":"#a78bfa","PFI Review":"#f59e0b",
  "Due Diligence":"#f97316","Approved":"#3b82f6","Disbursed":"#0d9488","Monitoring":"#22c55e"
};
const SEVERITY_COLORS = { High:"#ef4444", Medium:"#f59e0b", Low:"#60a5fa" };

// ── MSME Readiness Scorecard ─────────────────────────────────────────────────
const STAGE_COLORS_MSME = {
  "Investment Ready":"#22c55e","Nearly Ready":"#0d9488",
  "In Progress":"#f59e0b","Early Stage":"#ef4444"
};

function MSMEReadinessScorecard({ data, selected, onSelect }) {
  if (!data) return (
    <div style={{ display:"flex", justifyContent:"center", alignItems:"center", padding:48 }}>
      <div style={{ textAlign:"center" }}>
        <div style={{ width:28, height:28, border:"3px solid #0f2033", borderTopColor:"#34d399",
          borderRadius:"50%", animation:"spin 1s linear infinite", margin:"0 auto 10px" }} />
        <p style={{ fontSize:11, color:"#475569" }}>Loading MSME readiness data…</p>
      </div>
    </div>
  );

  const msmes = data.msmes || [];
  const summary = data.summary || {};
  const dimensions = data.dimensions || [];
  const sel = selected ? msmes.find(m => m.id === selected) : null;

  return (
    <div>
      {/* Summary stats */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:14 }}>
        {[
          {l:"Investment Ready",  v:summary.investment_ready, c:"#22c55e"},
          {l:"Nearly Ready",      v:summary.nearly_ready,     c:"#0d9488"},
          {l:"In Progress",       v:summary.in_progress,      c:"#f59e0b"},
          {l:"Early Stage",       v:summary.early_stage,      c:"#ef4444"},
        ].map(x => (
          <div key={x.l} style={{ background:"#0a1628", border:"1px solid #0f2033",
            borderTop:`3px solid ${x.c}`, borderRadius:10, padding:"10px 12px" }}>
            <p style={{ fontSize:20, fontWeight:900, color:x.c }}>{x.v}</p>
            <p style={{ fontSize:9, color:"#475569", textTransform:"uppercase", letterSpacing:"0.07em" }}>{x.l}</p>
          </div>
        ))}
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1.8fr", gap:16 }}>
        {/* MSME List */}
        <div>
          <p style={{ fontSize:10, fontWeight:700, color:"#64748b", textTransform:"uppercase",
            letterSpacing:"0.1em", marginBottom:10 }}>MSME Readiness Queue ({msmes.length})</p>
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            {msmes.map(m => {
              const sc = STAGE_COLORS_MSME[m.readiness_stage] || "#475569";
              return (
                <div key={m.id} onClick={() => onSelect(m.id)}
                  style={{ background: selected===m.id?"rgba(52,211,153,0.06)":"#060f1e",
                    border:`1px solid ${selected===m.id?"#34d399":"#0f2033"}`,
                    borderRadius:10, padding:"10px 12px", cursor:"pointer", transition:"all .15s" }}>
                  <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                    <p style={{ fontSize:10, fontWeight:700, color:"#f0f9ff" }}>{m.name}</p>
                    <span style={{ fontSize:16, fontWeight:900, color:sc }}>{m.weighted_score}</span>
                  </div>
                  <div style={{ display:"flex", gap:5, marginBottom:4, flexWrap:"wrap" }}>
                    <Badge label={m.readiness_stage} color={sc} />
                    <Badge label={m.stage} color="#475569" />
                    {m.women_owned && <Badge label="Women-owned" color="#ec4899" />}
                  </div>
                  <p style={{ fontSize:8, color:"#334155" }}>{m.sector} · {m.state} · {m.time_to_finance}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* MSME Detail */}
        {sel ? (
          <div>
            <div style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:14, padding:16 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:14 }}>
                <div>
                  <p style={{ fontSize:14, fontWeight:800, color:"#f0f9ff" }}>{sel.name}</p>
                  <p style={{ fontSize:10, color:"#475569" }}>{sel.sector} · {sel.state} · {sel.id}</p>
                </div>
                <div style={{ textAlign:"right" }}>
                  <p style={{ fontSize:22, fontWeight:900, color:STAGE_COLORS_MSME[sel.readiness_stage]||"#475569" }}>
                    {sel.weighted_score}/100
                  </p>
                  <Badge label={sel.readiness_stage} color={STAGE_COLORS_MSME[sel.readiness_stage]||"#475569"} />
                </div>
              </div>

              {/* Quick metrics */}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8, marginBottom:14 }}>
                {[
                  {l:"Financing Probability",  v:`${sel.financing_probability_pct}%`,   c:"#34d399"},
                  {l:"Time to Finance",         v:sel.time_to_finance,                   c:"#60a5fa"},
                  {l:"Finance Need",            v:fmtEur(sel.finance_need),              c:"#fbbf24"},
                  {l:"Revenue",                 v:`₦${fmtN(sel.revenue)}`,              c:"#a78bfa"},
                  {l:"Employees",               v:sel.employees,                         c:"#f97316"},
                  {l:"Recommended PFI",         v:sel.recommended_pfi,                  c:"#0d9488"},
                ].map(x => (
                  <div key={x.l} style={{ background:"#060f1e", borderRadius:8, padding:"7px 10px" }}>
                    <p style={{ fontSize:10, fontWeight:700, color:x.c }}>{x.v}</p>
                    <p style={{ fontSize:8, color:"#475569", textTransform:"uppercase", letterSpacing:"0.06em" }}>{x.l}</p>
                  </div>
                ))}
              </div>

              {/* Radar chart */}
              <div style={{ marginBottom:12 }}>
                <p style={{ fontSize:10, fontWeight:700, color:"#64748b", marginBottom:8 }}>Readiness Profile</p>
                <ResponsiveContainer width="100%" height={180}>
                  <RadarChart data={Object.entries(sel.dimension_scores || {}).map(([axis, value]) => ({ axis: axis.split(" ")[0], value }))}>
                    <PolarGrid stroke="#0f2033" />
                    <PolarAngleAxis dataKey="axis" tick={{ fill:"#475569", fontSize:7 }} />
                    <PolarRadiusAxis domain={[0,100]} tick={false} />
                    <Radar dataKey="value" stroke={STAGE_COLORS_MSME[sel.readiness_stage]||"#34d399"}
                      fill={STAGE_COLORS_MSME[sel.readiness_stage]||"#34d399"} fillOpacity={0.15} strokeWidth={2} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>

              {/* Dimension scores grid */}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:4, marginBottom:12 }}>
                {Object.entries(sel.dimension_scores || {}).map(([dim, score]) => (
                  <div key={dim} style={{ display:"flex", justifyContent:"space-between", alignItems:"center",
                    background:"#060f1e", borderRadius:5, padding:"4px 8px" }}>
                    <span style={{ fontSize:8, color:"#475569" }}>{dim}</span>
                    <span style={{ fontSize:9, fontWeight:700, color:scoreColor(score) }}>{score}</span>
                  </div>
                ))}
              </div>

              {/* Missing docs + TA */}
              {sel.missing?.length > 0 && (
                <div style={{ background:"#1a0a00", border:"1px solid #78350f30", borderRadius:8,
                  padding:"8px 12px", marginBottom:8 }}>
                  <p style={{ fontSize:9, color:"#f59e0b", fontWeight:700, marginBottom:4 }}>Missing Documents</p>
                  {sel.missing.map(d => (
                    <p key={d} style={{ fontSize:9, color:"#78350f" }}>• {d}</p>
                  ))}
                </div>
              )}

              {sel.ta_required !== "None" && (
                <div style={{ background:"#0a1628", borderRadius:8, padding:"8px 12px", marginBottom:8,
                  border:"1px solid #1e3a5f" }}>
                  <p style={{ fontSize:9, color:"#60a5fa", fontWeight:700, marginBottom:2 }}>TA Required</p>
                  <p style={{ fontSize:9, color:"#64748b" }}>{sel.ta_required}</p>
                </div>
              )}

              {/* AI recommendation */}
              <div style={{ background:"#001a0a", border:"1px solid #004a1e", borderRadius:8, padding:"8px 12px" }}>
                <p style={{ fontSize:9, color:"#34d399", fontWeight:700, marginBottom:3 }}>AI Recommendation</p>
                <p style={{ fontSize:9, color:"#065f46", lineHeight:1.5 }}>{sel.ai_recommendation}</p>
                <p style={{ fontSize:8, color:"#334155", marginTop:4 }}>
                  Next action: {sel.next_action} · Owner: {sel.owner}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div style={{ display:"flex", alignItems:"center", justifyContent:"center",
            background:"#0a1628", border:"1px solid #0f2033", borderRadius:14, padding:32, textAlign:"center" }}>
            <div>
              <p style={{ fontSize:12, color:"#334155", marginBottom:6 }}>Select an MSME to view readiness details</p>
              <p style={{ fontSize:9, color:"#1e3a5f" }}>Scores across 14 dimensions · AI recommendations · TA requirements</p>
            </div>
          </div>
        )}
      </div>

      {/* Workflow connector */}
      <div style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:14, padding:14, marginTop:14 }}>
        <SectionHeader title="MSME Financing Workflow" sub="From diagnosis to scale-up" />
        <div style={{ display:"flex", alignItems:"center", gap:0, overflowX:"auto" }}>
          {["MSME Diagnosis","Technical Assistance","Improved Readiness","PFI Submission","Financing","Monitoring","Scale-up"].map((step, i) => (
            <React.Fragment key={step}>
              <div style={{ background:"#060f1e", borderRadius:8, padding:"8px 12px", textAlign:"center", minWidth:100, flexShrink:0 }}>
                <p style={{ fontSize:9, fontWeight:700, color:["#ef4444","#f59e0b","#f59e0b","#60a5fa","#34d399","#0d9488","#22c55e"][i]||"#34d399" }}>
                  {step}
                </p>
              </div>
              {i < 6 && <div style={{ fontSize:12, color:"#1e3a5f", padding:"0 4px", flexShrink:0 }}>→</div>}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Pipeline Velocity Panel ────────────────────────────────────────────────────
function VelocityPanel({ velocity }) {
  const s = velocity?.summary || {};
  const stages = velocity?.velocity_stages || [];
  const bottlenecks = velocity?.bottlenecks || [];
  const monthlyFlow = velocity?.monthly_flow || [];

  return (
    <div style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:14, padding:16, marginBottom:16 }}>
      <SectionHeader title="Pipeline Velocity Intelligence" sub="Stage throughput · Bottleneck analysis · Quarterly projection" />

      {/* Summary KPIs */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:14 }}>
        {[
          { l:"Avg Cycle Days",          v:`${s.avg_total_cycle_days}d`,     c:"#60a5fa" },
          { l:"Q4 Projected Disbursement",v:fmtEur(s.q4_projected_disbursement), c:"#34d399" },
          { l:"Stalled >90 Days",        v:s.projects_stalled_90d,           c:"#f59e0b" },
          { l:"Overall Conversion Rate", v:`${s.conversion_rate_overall}%`,  c:"#a78bfa" },
        ].map(x => (
          <div key={x.l} style={{ background:"#060f1e", borderTop:`3px solid ${x.c}`,
            borderRadius:10, padding:"10px 12px" }}>
            <p style={{ fontSize:16, fontWeight:900, color:x.c }}>{x.v}</p>
            <p style={{ fontSize:9, color:"#475569", textTransform:"uppercase", letterSpacing:"0.08em", lineHeight:1.4 }}>{x.l}</p>
          </div>
        ))}
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1.5fr 1fr", gap:16 }}>
        {/* Stage velocity chart */}
        <div>
          <p style={{ fontSize:10, fontWeight:700, color:"#64748b", marginBottom:8 }}>Average Days per Stage</p>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={stages} margin={{ top:4, right:8, bottom:4, left:8 }}>
              <CartesianGrid stroke="#0f2033" strokeDasharray="3 3" />
              <XAxis dataKey="stage" tick={{ fill:"#475569", fontSize:7 }}
                tickFormatter={s => s.split(" ")[0]} />
              <YAxis tick={{ fill:"#475569", fontSize:8 }} />
              <Tooltip
                contentStyle={{ background:"#0a1628", border:"1px solid #1e3a5f", borderRadius:8, fontSize:10 }}
                labelStyle={{ color:"#f0f9ff" }} itemStyle={{ color:"#34d399" }}
              />
              <Bar dataKey="avg_days" fill="#34d399" radius={[3,3,0,0]} name="Avg Days" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Bottleneck alerts */}
        <div>
          <p style={{ fontSize:10, fontWeight:700, color:"#64748b", marginBottom:8 }}>AI Bottleneck Analysis</p>
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            {bottlenecks.map(b => (
              <div key={b.stage} style={{ background:"#060f1e", borderRadius:8, padding:"8px 10px",
                borderLeft:`3px solid ${SEVERITY_COLORS[b.severity]||"#475569"}` }}>
                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}>
                  <span style={{ fontSize:10, fontWeight:700, color:"#f0f9ff" }}>{b.stage}</span>
                  <Badge label={b.severity} color={SEVERITY_COLORS[b.severity]} />
                </div>
                <p style={{ fontSize:8.5, color:"#475569", marginBottom:3 }}>
                  {b.projects} projects · avg {b.avg_days}d · {b.conversion_pct}% conversion
                </p>
                <p style={{ fontSize:8, color:"#334155", lineHeight:1.4 }}>{b.action}</p>
              </div>
            ))}
            {bottlenecks.length === 0 && (
              <p style={{ fontSize:10, color:"#334155" }}>No critical bottlenecks detected.</p>
            )}
          </div>
        </div>
      </div>

      {/* Monthly flow */}
      <div style={{ marginTop:14 }}>
        <p style={{ fontSize:10, fontWeight:700, color:"#64748b", marginBottom:8 }}>Monthly Pipeline Flow</p>
        <div style={{ display:"flex", gap:6, overflowX:"auto" }}>
          {monthlyFlow.map(m => (
            <div key={m.month} style={{ background:"#060f1e", borderRadius:8, padding:"8px 12px",
              minWidth:100, textAlign:"center", flexShrink:0 }}>
              <p style={{ fontSize:8, color:"#475569", marginBottom:4 }}>{m.month}</p>
              <div style={{ display:"flex", justifyContent:"center", gap:8 }}>
                <div>
                  <p style={{ fontSize:12, fontWeight:700, color:"#34d399" }}>+{m.entered}</p>
                  <p style={{ fontSize:7, color:"#334155" }}>entered</p>
                </div>
                <div>
                  <p style={{ fontSize:12, fontWeight:700, color:"#60a5fa" }}>{m.exited}</p>
                  <p style={{ fontSize:7, color:"#334155" }}>advanced</p>
                </div>
                <div>
                  <p style={{ fontSize:12, fontWeight:700, color:"#fbbf24" }}>{m.disbursed}</p>
                  <p style={{ fontSize:7, color:"#334155" }}>disbursed</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Project Card ──────────────────────────────────────────────────────────────
function ProjectCard({ project, onClick }) {
  const rc = riskColor(project.risk);
  return (
    <div onClick={() => onClick(project)}
      style={{ background:"#07111e", border:"1px solid #0f2033", borderRadius:10, padding:"8px 10px",
        cursor:"pointer", transition:"all .15s", marginBottom:6 }}
      onMouseEnter={e => { e.currentTarget.style.borderColor="#1e3a5f"; e.currentTarget.style.background="#0a1628"; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor="#0f2033"; e.currentTarget.style.background="#07111e"; }}>
      <p style={{ fontSize:9.5, fontWeight:600, color:"#f0f9ff", lineHeight:1.3, marginBottom:4 }}>{project.name}</p>
      <p style={{ fontSize:8, color:"#475569", marginBottom:4 }}>{project.pfi} · {project.state}</p>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <span style={{ fontSize:9, fontWeight:700, color:"#34d399" }}>{fmtEur(project.amount)}</span>
        <div style={{ display:"flex", gap:3 }}>
          {project.green   ? <span style={{ fontSize:7, background:"#14532d", color:"#4ade80", padding:"1px 4px", borderRadius:3 }}>G</span> : null}
          {project.digital ? <span style={{ fontSize:7, background:"#1e3a5f", color:"#60a5fa", padding:"1px 4px", borderRadius:3 }}>D</span> : null}
        </div>
      </div>
      <div style={{ display:"flex", justifyContent:"space-between", marginTop:4 }}>
        <span style={{ fontSize:8, color:rc }}>{project.risk}</span>
        <span style={{ fontSize:8, color:scoreColor(project.readiness) }}>{project.readiness}/100</span>
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function DBNPipeline() {
  const [data, setData] = useState(null);
  const [velocity, setVelocity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterSector, setFilterSector] = useState("all");
  const [filterFacility, setFilterFacility] = useState("all");
  const [filterRisk, setFilterRisk] = useState("all");
  const [searchQ, setSearchQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [viewMode, setViewMode] = useState("kanban");
  const [showVelocity, setShowVelocity] = useState(true);
  const [msmeData, setMsmeData] = useState(null);
  const [msmeSelected, setMsmeSelected] = useState(null);

  useEffect(() => {
    Promise.all([dbnApi.pipeline(), dbnApi.pipelineVelocity()])
      .then(([r1, r2]) => { setData(r1.data); setVelocity(r2.data); })
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingState />;
  const pipeline = data?.pipeline || {};
  const pfis = data?.pfis || [];
  const stats = data?.pipeline_stats || {};

  const allProjects = Object.entries(pipeline).flatMap(([stage, ps]) => ps.map(p => ({...p, stage})));
  const filtered = allProjects.filter(p =>
    (filterSector === "all" || p.sector === filterSector) &&
    (filterRisk   === "all" || p.risk === filterRisk) &&
    (filterFacility === "all" || p.facility_id === filterFacility) &&
    (searchQ === "" || p.name.toLowerCase().includes(searchQ.toLowerCase()) || p.pfi.toLowerCase().includes(searchQ.toLowerCase()))
  );

  const sectors = [...new Set(allProjects.map(p => p.sector))];

  return (
    <div style={{ display:"flex", flexDirection:"column", minHeight:"100%" }}>
      <PageHeader
        title="PFI and MSME Pipeline"
        subtitle="Velocity intelligence · Kanban board · PFI readiness · Pipeline management">
        <div style={{ display:"flex", gap:4 }}>
          <button onClick={() => setShowVelocity(v => !v)}
            style={{ padding:"4px 10px", borderRadius:6, fontSize:9, fontWeight:700, border:"1px solid #1e3a5f",
              background:showVelocity?"#1e3a5f":"transparent", color:showVelocity?"#93c5fd":"#475569", cursor:"pointer" }}>
            {showVelocity?"Hide Velocity":"Show Velocity"}
          </button>
          {["kanban","table","msme"].map(m => (
            <button key={m} onClick={() => {
              setViewMode(m);
              if (m === "msme" && !msmeData) {
                dbnApi.msmeReadiness().then(r => setMsmeData(r.data)).catch(() => {});
              }
            }}
              style={{ padding:"4px 10px", borderRadius:6, fontSize:9, fontWeight:700, border:"none", cursor:"pointer",
                background:viewMode===m?"#1e3a5f":"#0a1628", color:viewMode===m?"#93c5fd":"#475569" }}>
              {m === "kanban" ? "Kanban" : m === "table" ? "Table" : "MSME Readiness"}
            </button>
          ))}
        </div>
      </PageHeader>

      <div style={{ padding:"16px 24px", flex:1 }}>
        {/* Stats */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:14 }}>
          {[
            { l:"Total Projects",  v:stats.total,       c:"#60a5fa" },
            { l:"Active",          v:stats.active,      c:"#22c55e" },
            { l:"In Pipeline",     v:stats.in_pipeline, c:"#f59e0b" },
            { l:"Active PFIs",     v:pfis.filter(p=>p.status==="Active").length, c:"#34d399" },
          ].map(x => (
            <div key={x.l} style={{ background:"#0a1628", border:"1px solid #0f2033",
              borderTop:`3px solid ${x.c}`, borderRadius:10, padding:"10px 12px" }}>
              <p style={{ fontSize:20, fontWeight:900, color:x.c }}>{x.v}</p>
              <p style={{ fontSize:9, color:"#475569", textTransform:"uppercase", letterSpacing:"0.08em" }}>{x.l}</p>
            </div>
          ))}
        </div>

        {/* Velocity Panel (collapsible) */}
        {showVelocity && <VelocityPanel velocity={velocity} />}

        {/* Filters */}
        <div style={{ display:"flex", gap:8, marginBottom:14, flexWrap:"wrap" }}>
          <div style={{ display:"flex", alignItems:"center", gap:6, background:"#0a1628",
            border:"1px solid #0f2033", borderRadius:8, padding:"5px 10px" }}>
            <Search size={11} color="#475569" />
            <input value={searchQ} onChange={e => setSearchQ(e.target.value)}
              placeholder="Search projects or PFIs…"
              style={{ background:"none", border:"none", color:"#f0f9ff", fontSize:10, outline:"none", width:160 }} />
          </div>
          {[
            { val:filterSector, set:setFilterSector, opts:[{val:"all",lbl:"All Sectors"},...sectors.map(s=>({val:s,lbl:s}))], label:"Sector" },
            { val:filterRisk,   set:setFilterRisk,   opts:[{val:"all",lbl:"All Risk"},{val:"Low",lbl:"Low"},{val:"Moderate",lbl:"Moderate"},{val:"High",lbl:"High"}], label:"Risk" },
            { val:filterFacility, set:setFilterFacility, opts:[{val:"all",lbl:"All Facilities"},{val:"FAC-EIB",lbl:"EIB"},{val:"FAC-AFDB",lbl:"AfDB"},{val:"FAC-KFW",lbl:"KfW"},{val:"FAC-WB",lbl:"World Bank"}], label:"Facility" },
          ].map(f => (
            <select key={f.label} value={f.val} onChange={e => f.set(e.target.value)}
              style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:8,
                padding:"5px 10px", fontSize:10, color:"#94a3b8" }}>
              {f.opts.map(o => <option key={o.val} value={o.val}>{o.lbl}</option>)}
            </select>
          ))}
          <span style={{ fontSize:10, color:"#475569", alignSelf:"center" }}>{filtered.length} projects</span>
        </div>

        {/* Kanban */}
        {viewMode === "kanban" && (
          <div style={{ display:"flex", gap:10, overflowX:"auto", paddingBottom:8 }}>
            {STAGE_ORDER.map(stage => {
              const stageProjects = filtered.filter(p => p.stage === stage);
              const color = STAGE_COLORS[stage] || "#64748b";
              return (
                <div key={stage} style={{ minWidth:180, flex:1 }}>
                  <div style={{ background:`${color}20`, border:`1px solid ${color}40`,
                    borderRadius:8, padding:"6px 10px", marginBottom:8, textAlign:"center" }}>
                    <p style={{ fontSize:9, fontWeight:700, color, textTransform:"uppercase", letterSpacing:"0.06em" }}>{stage}</p>
                    <p style={{ fontSize:14, fontWeight:900, color:"#f0f9ff" }}>{stageProjects.length}</p>
                    <p style={{ fontSize:8, color:"#475569" }}>{fmtEur(stageProjects.reduce((a,p)=>a+p.amount,0))}</p>
                  </div>
                  {stageProjects.map(p => (
                    <ProjectCard key={p.id} project={p} onClick={setSelected} />
                  ))}
                </div>
              );
            })}
          </div>
        )}

        {/* Table */}
        {viewMode === "table" && (
          <div style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:14, overflow:"hidden" }}>
            <table style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead>
                <tr>
                  {["ID","Project Name","Sector","State","PFI","Finance","Stage","Risk","Readiness","Green","Digital"].map(h => (
                    <th key={h} style={{ background:"#060f1e", color:"#475569", fontSize:8.5, fontWeight:700,
                      padding:"8px 10px", textAlign:"left", textTransform:"uppercase", letterSpacing:"0.07em" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(p => (
                  <tr key={p.id} onClick={() => setSelected(p)} style={{ cursor:"pointer" }}
                    onMouseEnter={e => e.currentTarget.style.background="rgba(52,211,153,0.04)"}
                    onMouseLeave={e => e.currentTarget.style.background=""}>
                    <td style={{ padding:"7px 10px", fontSize:9, color:"#475569", borderBottom:"1px solid #0f1e33" }}>{p.id}</td>
                    <td style={{ padding:"7px 10px", fontSize:10, color:"#f0f9ff", borderBottom:"1px solid #0f1e33", maxWidth:200 }}>{p.name}</td>
                    <td style={{ padding:"7px 10px", fontSize:9, color:"#64748b", borderBottom:"1px solid #0f1e33" }}>{p.sector}</td>
                    <td style={{ padding:"7px 10px", fontSize:9, color:"#64748b", borderBottom:"1px solid #0f1e33" }}>{p.state}</td>
                    <td style={{ padding:"7px 10px", fontSize:9, color:"#64748b", borderBottom:"1px solid #0f1e33" }}>{p.pfi}</td>
                    <td style={{ padding:"7px 10px", fontSize:10, color:"#34d399", fontWeight:700, borderBottom:"1px solid #0f1e33" }}>{fmtEur(p.amount)}</td>
                    <td style={{ padding:"7px 10px", borderBottom:"1px solid #0f1e33" }}>
                      <Badge label={p.stage} color={STAGE_COLORS[p.stage]||"#475569"} />
                    </td>
                    <td style={{ padding:"7px 10px", fontSize:9, color:riskColor(p.risk), fontWeight:600, borderBottom:"1px solid #0f1e33" }}>{p.risk}</td>
                    <td style={{ padding:"7px 10px", fontSize:10, color:scoreColor(p.readiness), fontWeight:700, borderBottom:"1px solid #0f1e33" }}>{p.readiness}</td>
                    <td style={{ padding:"7px 10px", borderBottom:"1px solid #0f1e33", textAlign:"center" }}>
                      {p.green ? <span style={{ color:"#22c55e" }}>Yes</span> : <span style={{ color:"#334155" }}>No</span>}
                    </td>
                    <td style={{ padding:"7px 10px", borderBottom:"1px solid #0f1e33", textAlign:"center" }}>
                      {p.digital ? <span style={{ color:"#60a5fa" }}>Yes</span> : <span style={{ color:"#334155" }}>No</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* MSME Readiness Scorecard */}
        {viewMode === "msme" && (
          <MSMEReadinessScorecard data={msmeData} selected={msmeSelected} onSelect={setMsmeSelected} />
        )}

        {/* PFI Readiness */}
        <div style={{ marginTop:16, background:"#0a1628", border:"1px solid #0f2033", borderRadius:14, padding:16 }}>
          <SectionHeader title="PFI Readiness Benchmarking" sub="Composite score across green, digital, ESG, data quality and reporting dimensions" />
          <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10 }}>
            {pfis.slice(0,6).map(p => (
              <div key={p.id} style={{ background:"#060f1e", borderRadius:10, padding:"10px 12px" }}>
                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
                  <div>
                    <p style={{ fontSize:10, fontWeight:700, color:"#f0f9ff" }}>{p.name}</p>
                    <p style={{ fontSize:8, color:"#475569" }}>{p.type} · Tier {p.tier} · {p.status}</p>
                  </div>
                  <span style={{ fontSize:16, fontWeight:900, color:scoreColor(p.composite_score||p.readiness_score) }}>
                    {p.composite_score||p.readiness_score}
                  </span>
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:3 }}>
                  {[["Green",p.green_score],["Digital",p.digital_score],["ESG",p.esg_score],["Reporting",p.reporting_completeness]].map(([l,v]) => (
                    <div key={l} style={{ display:"flex", justifyContent:"space-between" }}>
                      <span style={{ fontSize:8, color:"#334155" }}>{l}</span>
                      <span style={{ fontSize:8, fontWeight:700, color:scoreColor(v) }}>{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Project detail modal */}
      {selected && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.7)", zIndex:2000,
          display:"flex", alignItems:"center", justifyContent:"center" }}
          onClick={() => setSelected(null)}>
          <div style={{ background:"#0a1628", border:"1px solid #1e3a5f", borderRadius:16,
            width:440, maxHeight:"80vh", overflowY:"auto", padding:20 }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display:"flex", justifyContent:"space-between", marginBottom:14 }}>
              <p style={{ fontSize:14, fontWeight:800, color:"#f0f9ff" }}>{selected.name}</p>
              <button onClick={() => setSelected(null)} style={{ background:"none", border:"none", color:"#475569", cursor:"pointer", fontSize:18 }}>✕</button>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
              {[
                ["Project ID", selected.id], ["Stage", selected.stage],
                ["PFI", selected.pfi], ["Facility", selected.facility_id],
                ["Sector", selected.sector], ["State", selected.state],
                ["Finance Amount", fmtEur(selected.amount)], ["Risk Rating", selected.risk],
                ["Readiness Score", `${selected.readiness}/100`], ["Additionality", `${selected.additionality||"—"}/100`],
                ["Green Eligible", selected.green?"Yes":"No"], ["Digital Eligible", selected.digital?"Yes":"No"],
              ].map(([l,v]) => (
                <div key={l} style={{ background:"#060f1e", borderRadius:6, padding:"6px 10px" }}>
                  <p style={{ fontSize:8, color:"#475569", marginBottom:1 }}>{l}</p>
                  <p style={{ fontSize:10, fontWeight:600, color:"#f0f9ff" }}>{v}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
